import { CheckCircle2, Circle, Megaphone, Users, Handshake } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useCallPromptsSuppressed } from '../hooks/useLaunchCall'

const PINK = '#FB3AAE'

/**
 * GetStartedChecklist — the self-serve activation backbone on Home.
 *
 * Three steps (dev call 2026-09-04: the onboarding-call step is gone — the
 * call is now a managed-services pitch that lives in ManagedServicesBanner,
 * not an activation step). Auto-hides once all three are complete. No
 * dismiss — an incomplete brand keeps seeing it by design. Hidden entirely
 * for subscribed (grandfathered paid) brands by the caller.
 *
 * Also hidden for any brand under an AGENCY — the agency runs its roster's
 * activation from its own lobby checklist, so a brand inside one should not be
 * handed a second, conflicting to-do list.
 */
export default function GetStartedChecklist({
  hasCampaign,
  hasApplications,
  hasHire,
  onPostJob,
}: {
  hasCampaign: boolean
  hasApplications: boolean
  hasHire: boolean
  onPostJob: () => void
}) {
  // Nothing at all for a brand under an agency — see the note above. Gated
  // inside the component rather than at Home's call sites so a new one can't
  // reintroduce it.
  const promptsSuppressed = useCallPromptsSuppressed()
  const steps = [
    {
      key: 'job',
      icon: Megaphone,
      title: 'Post your first job',
      sub: 'One campaign = one product. Add a card or wallet credit so it can go live, then creators start applying within hours — nothing is charged until you hire.',
      done: hasCampaign,
    },
    {
      key: 'apps',
      icon: Users,
      title: 'Get applications',
      sub: 'Brands typically receive hundreds of applications in the first few days.',
      done: hasApplications,
    },
    {
      key: 'hire',
      icon: Handshake,
      title: 'Make your first hire',
      sub: "Pay per contracted video. If a creator doesn't deliver, you get every dollar back.",
      done: hasHire,
    },
  ]
  const doneCount = steps.filter(s => s.done).length
  if (promptsSuppressed) return null
  if (doneCount === steps.length) return null

  return (
    <div
      style={{
        background: 'var(--bg-secondary)',
        border: '1px solid var(--border-primary)',
        borderRadius: 14,
        padding: '20px 22px',
        marginBottom: 28,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 14 }}>
        <div style={{ fontSize: 15.5, fontWeight: 700, color: 'var(--text-primary)' }}>
          Get started on BMC
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--text-tertiary)' }}>
          {doneCount} of {steps.length} done
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {steps.map(step => {
          const Icon = step.icon
          const isNext = !step.done && steps.find(s => !s.done)?.key === step.key
          return (
            <div
              key={step.key}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                padding: '10px 12px',
                borderRadius: 10,
                background: isNext ? 'rgba(251, 58, 174, 0.07)' : 'transparent',
                border: isNext ? '1px solid rgba(251, 58, 174, 0.25)' : '1px solid transparent',
              }}
            >
              {step.done
                ? <CheckCircle2 size={18} style={{ color: '#22c55e', flexShrink: 0, marginTop: 1 }} />
                : <Circle size={18} style={{ color: isNext ? PINK : 'var(--text-tertiary)', flexShrink: 0, marginTop: 1 }} />}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                }}>
                  <Icon size={14} style={{ color: step.done ? 'var(--text-tertiary)' : 'var(--text-secondary)' }} />
                  <span style={{
                    fontSize: 14, fontWeight: 600,
                    color: step.done ? 'var(--text-tertiary)' : 'var(--text-primary)',
                    textDecoration: step.done ? 'line-through' : 'none',
                  }}>
                    {step.title}
                  </span>
                </div>
                {!step.done && (
                  <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 3, lineHeight: 1.45 }}>
                    {step.sub}
                  </div>
                )}
                {/* Inline action on the active step only */}
                {isNext && step.key === 'job' && (
                  <button
                    onClick={onPostJob}
                    style={{
                      display: 'inline-block', marginTop: 10,
                      background: PINK, color: '#fff', border: 'none', cursor: 'pointer',
                      padding: '9px 16px', borderRadius: 999, fontSize: 13, fontWeight: 700,
                      fontFamily: 'inherit',
                    }}
                  >
                    Post a job
                  </button>
                )}
              </div>
            </div>
          )
        })}
      </div>
      <div style={{ marginTop: 10, fontSize: 12.5, color: 'var(--text-tertiary)' }}>
        New to hiring creators?{' '}
        <Link to="/how-it-works" style={{ color: 'var(--text-secondary)', textDecoration: 'underline', textUnderlineOffset: 2 }}>
          See how it works and what creators cost
        </Link>
      </div>
    </div>
  )
}
